import { getChartConfiguration } from "./chart-configuration"
import { getDefaultDataLabelsConfiguration } from "./chart-datalabels"
import { colors } from "./colors"
import { environments } from "./environments"
import { createSingleGradient } from "./helpers/gradient"
import { views } from "./helpers/views"

/*

Datasets

[0] Kill switch static line (locally generated? as generally a fixed value, I heard)
[1] l1
[2] l2
[3] l3

*/

const getVoltageChartConfiguration = (date, view, lang, type, fullscreen, debug = false, environment = "apps", timezone) => {
    let chartConfiguration = getChartConfiguration(date, view, lang, type, fullscreen, debug, environment, false, timezone)

    // all line graphs
    chartConfiguration.type = 'line'

    chartConfiguration.options.scales.yAxes[0].ticks.beginAtZero = false

    chartConfiguration.options.scales.yAxes[0].ticks.precision = 0

    chartConfiguration.options.scales.xAxes[0].offset = !fullscreen && views[view] >= views.week

    // Patch in a fourth dataset (l3) // Default config has 3 (empty) sets defined
    chartConfiguration.data.datasets[3] = {
        fill: 'origin',
        pointRadius: debug ? 3 : 0,
        borderWidth: views[view] <= views.day ? 2 : 1,
        borderSkipped: false,
        barThickness: views[view] == views.year ? environments(environment).barThickness : 10,
        minBarLength: 1,
        data: [],
        datalabels: getDefaultDataLabelsConfiguration(2),
        ...(debug && {pointBorderColor: 'red'}),
        ...(debug && {pointBackgroundColor: 'red'}),
    }

    chartConfiguration.data.datasets[0].borderWidth = 6

    // getChartConfiguration has an additional three_phases check
    // which we can't just set without issues in this case.
    // It will set hw.three_phases to true and that causes other code to activate like the tooltips logic.
    chartConfiguration.data.datasets[1].borderWidth = views[view] <= views.day ? 2 : 1

    // all datasets
    for(const i in chartConfiguration.data.datasets) {
        chartConfiguration.data.datasets[i].datalabels = {
            display: false,
        }

        // If we only have 1 data point, we show it as a point.
        // A line graph with one point is not a line.
        chartConfiguration.data.datasets[i].pointRadius = (context) => {
            const { dataset } = context
            const datasetLength = dataset.data.filter(dataPoint => dataPoint.y !== null).length

            return datasetLength === 1 ? 4 : 0
        }

        chartConfiguration.data.datasets[i].fill = 'start'

        // The 'monotone' algorithm is more suited to y = f(x) datasets : it preserves monotonicity
        // (or piecewise monotonicity) of the dataset being interpolated, and ensures local extremums (if any)
        // stay at input data points.
        chartConfiguration.data.datasets[i].cubicInterpolationMode = 'monotone'
    }

    // [0] limit
    chartConfiguration.data.datasets[0] = {
        ...chartConfiguration.data.datasets[0],
        borderCapStyle: "round",
        borderWidth: 2,
        borderDash: [1, 10],
        order: 0,
        borderColor: colors.voltage.limit_border
    }

    // [1] l1
    chartConfiguration.data.datasets[1] = {
        ...chartConfiguration.data.datasets[1],
        order: 1,
        borderWidth: 2,
        borderColor: colors.voltage.l1_border
    }

    // [2] l2
    chartConfiguration.data.datasets[2] = {
        ...chartConfiguration.data.datasets[2],
        order: 2,
        borderWidth: 2,
        borderColor: colors.voltage.l2_border
    }

    // [3] l3
    chartConfiguration.data.datasets[3] = {
        ...chartConfiguration.data.datasets[3],
        order: 3,
        borderWidth: 2,
        borderColor: colors.voltage.l3_border
    }

    // override "beforeUpdate" of chart-configuration.js
    chartConfiguration.plugins[0].beforeUpdate = (chart, options) => {

        const limit = chart.data.datasets[0]?.data[0]?.y ?? 0
        const offset = 1.0

        // Get the lowest value of all datasets
        let min = chart.data.datasets.reduce((min, set) =>
            set.data.reduce((min, p) => min < p.y || p.y === null ? min : p.y, min) ?? 0, min) ?? 0
        let max = limit

        // Calculate max values for each dataset and store in dataset.max
        // This value is used later to construct the right gradient
        chart.data.datasets.forEach((dataset, index) => {
            if (dataset.data.length === 0) {
                return
            }

            dataset.max = dataset.data.reduce((max, p) => p.y > max ? p.y : max, 0)

            max = Math.max(max, dataset.max)
        })

        const suggestedMin = min - offset
        const suggestedMax = Math.max(max, limit) + offset

        chart.options.scales.yAxes[0].ticks.suggestedMin = suggestedMin
        chart.options.scales.yAxes[0].ticks.suggestedMax = suggestedMax

        // set the "min" of all datasets to the absolute minimum value
        // this is needed to create the right gradient
        // -- min is not used in this context
        // But we might need it if we want to have the gradients not overlap other datasets?

        // chart.data.datasets.forEach(set => {
        //     set.min = suggestedMin
        // });
        // chart.data.datasets[0].min = suggestedMin
        // chart.data.datasets[1].min = suggestedMin
        // chart.data.datasets[2].min = suggestedMin
        // chart.data.datasets[3].min = suggestedMin
    }

    // Overrules gradient drawings
    chartConfiguration.plugins[0].afterLayout = (chart, options) => {

        chart.data.datasets[1].backgroundColor = createSingleGradient(
            chart,
            1,
            colors.voltage.l1_gradient_start,
            colors.voltage.l1_gradient_end,
        )

        chart.data.datasets[2].backgroundColor = createSingleGradient(
            chart,
            2,
            colors.voltage.l2_gradient_start,
            colors.voltage.l2_gradient_end,
        )

        chart.data.datasets[3].backgroundColor = createSingleGradient(
            chart,
            3,
            colors.voltage.l3_gradient_start,
            colors.voltage.l3_gradient_end,
        )
    }

    return chartConfiguration
}

export { getVoltageChartConfiguration }
